import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { X, Plus, Quotes, Trash, Gauge, Pencil } from "@phosphor-icons/react";

export function ConfidenceLabel({ score }) {
  if (score >= 8) return <span className="text-emerald-400 text-xs font-medium">Strong voice signal — your examples were consistent</span>;
  if (score >= 5) return <span className="text-amber-400 text-xs font-medium">Good foundation — consider adding more examples for precision</span>;
  return <span className="text-red-400 text-xs font-medium">Low confidence — your examples may be inconsistent or too short</span>;
}

export function ConfidenceMeter({ score }) {
  const pct = (score / 10) * 100;
  const color = score >= 8 ? "#10b981" : score >= 5 ? "#f59e0b" : "#ef4444";
  return (
    <div className="flex items-center gap-3" data-testid="confidence-meter">
      <div className="flex-1 h-2 rounded-full bg-[var(--bg-base)] overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease: "easeOut" }} className="h-full rounded-full" style={{ backgroundColor: color }} />
      </div>
      <span className="text-sm font-mono font-bold" style={{ color }}>{score}/10</span>
    </div>
  );
}

export function ChipEditor({ items, onChange, color = "default", placeholder = "Add..." }) {
  const [input, setInput] = useState("");
  const add = () => {
    const val = input.trim();
    if (val && !items.includes(val)) {
      onChange([...items, val]);
      setInput("");
    }
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const chipColor = color === "red"
    ? "bg-red-500/10 text-red-400 border-red-500/20"
    : "bg-[#FF4400]/10 text-[#FF4400] border-[#FF4400]/20";

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <span key={`${item}-${i}`} className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border ${chipColor}`}>
            {item}
            <button onClick={() => remove(i)} className="opacity-60 hover:opacity-100 transition-opacity"><X size={10} weight="bold" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} className="h-8 text-xs bg-[var(--bg-base)] border-[var(--border-strong)] flex-1" />
        <Button variant="outline" onClick={add} className="h-10 sm:h-8 text-xs border-[var(--border-strong)]"><Plus size={12} /></Button>
      </div>
    </div>
  );
}

export function VoiceSection({ title, children }) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4 space-y-3">
      <span className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">{title}</span>
      {children}
    </div>
  );
}

export function InlineDiff({ label, prev, curr }) {
  const added = curr.filter((c) => !prev.includes(c));
  const removed = prev.filter((c) => !curr.includes(c));
  if (!added.length && !removed.length) return null;
  return (
    <div className="space-y-1">
      <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{label}</span>
      <div className="flex flex-wrap gap-1">
        {added.map((c, i) => (
          <span key={`a-${i}`} className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">+ {c}</span>
        ))}
        {removed.map((c, i) => (
          <span key={`r-${i}`} className="px-2 py-0.5 text-[10px] rounded-full bg-red-500/10 text-red-400 border border-red-500/20 line-through">- {c}</span>
        ))}
      </div>
    </div>
  );
}

export function VoiceSummaryEditor({ voiceProfile, onUpdate }) {
  return (
    <div className="bg-[#FF4400]/5 border border-[#FF4400]/20 rounded-lg p-5 space-y-3" data-testid="voice-summary-section">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-[#FF4400] uppercase tracking-wider">Voice Summary — used in all generations</span>
        <Pencil size={14} className="text-[var(--text-muted)]" />
      </div>
      <Textarea value={voiceProfile.voice_summary} onChange={(e) => onUpdate("voice_summary", e.target.value)} rows={3} className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm resize-y" data-testid="voice-summary-textarea" />
    </div>
  );
}

export function SamplePhrases({ phrases, onUpdate }) {
  return (
    <div className="space-y-2">
      {(phrases || []).map((phrase, i) => (
        <div key={`phrase-${phrase.slice(0, 20)}-${i}`} className="flex items-start gap-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-3 group">
          <Quotes size={16} className="text-[#FF4400] mt-0.5 shrink-0" />
          <p className="text-sm text-[var(--text-secondary)] flex-1 italic">"{phrase}"</p>
          <button onClick={() => onUpdate(phrases.filter((_, j) => j !== i))} className="opacity-0 group-hover:opacity-100 p-1 text-[var(--text-muted)] hover:text-red-400 transition-all">
            <Trash size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}
